/**
 * useModeration - Hook for text moderation
 * 
 * A reusable hook that provides text moderation functionality
 * for any form field across the application.
 */

import { useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Moderation contexts - use these when calling checkModeration
export const MODERATION_CONTEXTS = {
  // Public-facing content (STRICT)
  BUSINESS_NAME: 'business_name',
  BUSINESS_DESCRIPTION: 'business_description',
  ORGANIZATION_NAME: 'organization_name',
  ORGANIZATION_DESCRIPTION: 'organization_description',
  NEWS_TITLE: 'news_title',
  NEWS_CONTENT: 'news_content',
  JOB_TITLE: 'job_title',
  JOB_DESCRIPTION: 'job_description',
  PROGRAM_TITLE: 'program_title',
  PROGRAM_DESCRIPTION: 'program_description',
  COMMENT: 'comment',
  REVIEW: 'review',
  
  // Semi-public content (MODERATE)
  CONTACT_FORM: 'contact_form',
  PROFILE_BIO: 'profile_bio',
  
  // Private/Sensitive content (LENIENT)
  DENUNCIATION_REPORT: 'denunciation_report',
  SUPPORT_MESSAGE: 'support_message',
  
  // Default
  GENERAL: 'general',
};

// Moderation actions returned by the API
export const MODERATION_ACTIONS = {
  APPROVED: 'approved',
  BLOCKED: 'blocked',
  PENDING_REVIEW: 'pending_review',
};

/**
 * useModeration hook
 * 
 * @returns {Object} Moderation utilities
 * @returns {Function} checkModeration - Check a single text field
 * @returns {Function} checkMultipleFields - Check multiple fields at once
 * @returns {boolean} isChecking - Whether moderation check is in progress
 * @returns {Object|null} lastResult - Result of last moderation check
 */
export function useModeration() {
  const [isChecking, setIsChecking] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  /**
   * Check a single text field for moderation issues
   * 
   * @param {string} text - Text to check
   * @param {string} context - Context from MODERATION_CONTEXTS
   * @returns {Promise<Object>} Moderation result with action and issues
   */
  const checkModeration = useCallback(async (text, context = MODERATION_CONTEXTS.GENERAL) => {
    if (!text || !text.trim()) {
      const result = { action: MODERATION_ACTIONS.APPROVED, issues: [], message: 'Texto vazio' };
      setLastResult(result);
      return result;
    }

    setIsChecking(true);
    try {
      const response = await axios.post(`${API}/moderation/check`, {
        text,
        context,
      });
      
      setLastResult(response.data);
      return response.data;
    } catch (error) {
      console.error('Erro ao verificar moderação:', error);
      // On error, default to approved to not block submissions
      const result = { action: MODERATION_ACTIONS.APPROVED, issues: [], message: 'Erro na verificação' };
      setLastResult(result);
      return result;
    } finally {
      setIsChecking(false);
    }
  }, []);

  /**
   * Check multiple fields at once
   * 
   * @param {Array<{field: string, text: string, context: string}>} fields - Array of fields to check
   * @returns {Promise<Object>} Object with field names as keys and moderation results as values
   */
  const checkMultipleFields = useCallback(async (fields) => {
    if (!fields || fields.length === 0) {
      return {};
    }

    setIsChecking(true);
    const results = {};

    try {
      // Check all fields in parallel
      const promises = fields.map(async ({ field, text, context }) => {
        if (!text || !text.trim()) {
          return { field, result: { action: MODERATION_ACTIONS.APPROVED, issues: [] } };
        }
        
        try {
          const response = await axios.post(`${API}/moderation/check`, {
            text,
            context: context || MODERATION_CONTEXTS.GENERAL,
          });
          return { field, result: response.data };
        } catch (error) {
          console.error(`Erro ao verificar moderação para ${field}:`, error);
          return { field, result: { action: MODERATION_ACTIONS.APPROVED, issues: [] } };
        }
      });

      const responses = await Promise.all(promises);
      responses.forEach(({ field, result }) => {
        results[field] = result;
      });

    } catch (error) {
      console.error('Erro ao verificar múltiplos campos:', error);
    } finally {
      setIsChecking(false);
    }

    return results;
  }, []);

  /**
   * Check if any result in an object has blocking issues
   * 
   * @param {Object} results - Object with moderation results
   * @returns {boolean} True if any field is blocked
   */
  const hasBlockingIssues = useCallback((results) => {
    if (!results) return false;
    return Object.values(results).some(r => r.action === MODERATION_ACTIONS.BLOCKED);
  }, []);

  /**
   * Get all blocking fields from results
   * 
   * @param {Object} results - Object with moderation results
   * @returns {Array<string>} Array of field names that are blocked
   */
  const getBlockedFields = useCallback((results) => {
    if (!results) return [];
    return Object.entries(results)
      .filter(([_, r]) => r.action === MODERATION_ACTIONS.BLOCKED)
      .map(([field, _]) => field);
  }, []);

  return {
    checkModeration,
    checkMultipleFields,
    hasBlockingIssues,
    getBlockedFields,
    isChecking,
    lastResult,
  };
}

/**
 * ModerationAlert Component
 * 
 * Display moderation issues inline with a form field
 */
export function ModerationAlert({ result, className = '' }) {
  if (!result || result.action === MODERATION_ACTIONS.APPROVED) {
    return null;
  }

  const isBlocked = result.action === MODERATION_ACTIONS.BLOCKED;
  
  return (
    <div 
      className={`mt-1 p-2 rounded-lg text-sm ${
        isBlocked 
          ? 'bg-red-50 border border-red-200 text-red-700' 
          : 'bg-yellow-50 border border-yellow-200 text-yellow-700'
      } ${className}`}
      role="alert"
      data-testid="moderation-alert"
    >
      <p className="font-medium">
        {isBlocked ? '⚠️ Conteúdo bloqueado' : '⏳ Conteúdo será revisado'}
      </p>
      <p className="text-xs mt-1">{result.message}</p>
      {result.issues && result.issues.length > 0 && (
        <ul className="mt-1 text-xs list-disc list-inside">
          {result.issues.map((issue, idx) => (
            <li key={idx}>{issue.description}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * withModeration HOC
 * 
 * Wrap a form component to add moderation capabilities
 */
export function withModeration(WrappedComponent) {
  return function ModerationWrapper(props) {
    const moderation = useModeration();
    return <WrappedComponent {...props} moderation={moderation} />;
  };
}

export default useModeration;
